import { useState } from 'react';
import { cn } from '../lib/utils';

const BODY_IMAGE = '/assets/images/body-map.png';

const SEVERITY = {
  severe: { color: '#ef4444', glow: '0 0 18px 4px rgba(239,68,68,0.55)', badge: 'bg-red-500 text-white', label: 'Severe' },
  moderate: { color: '#f59e0b', glow: '0 0 16px 4px rgba(245,158,11,0.5)', badge: 'bg-amber-500 text-white', label: 'Moderate' },
  mild: { color: '#22c55e', glow: '0 0 14px 4px rgba(34,197,94,0.45)', badge: 'bg-emerald-500 text-white', label: 'Mild' },
};

/* Hotspot positions as % of image dimensions */
const HOTSPOTS = {
  head:      { top: '4%',  left: '50%', label: 'Head / Brain' },
  chest:     { top: '26%', left: '50%', label: 'Heart / Chest' },
  lungs:     { top: '24%', left: '36%', label: 'Lungs', rightClone: '64%' },
  abdomen:   { top: '40%', left: '50%', label: 'Abdomen / GI' },
  left_arm:  { top: '34%', left: '18%', label: 'Left Arm' },
  right_arm: { top: '34%', left: '82%', label: 'Right Arm' },
  left_leg:  { top: '70%', left: '38%', label: 'Left Leg' },
  right_leg: { top: '70%', left: '62%', label: 'Right Leg' },
  spine:     { top: '35%', left: '50%', label: 'Spine / Back' },
  skin:      { top: '18%', left: '26%', label: 'Skin' },
};

export const BodyMap = ({ regions = {} }) => {
  const [active, setActive] = useState(null);
  const affectedCount = Object.keys(regions).length;

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white rounded-xl border border-slate-200 p-5" data-testid="body-map-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">3D Body Health Map</h3>
        <span className="text-[10px] text-slate-400 font-medium">
          {affectedCount > 0 ? `${affectedCount} region${affectedCount > 1 ? 's' : ''} affected` : 'No issues'}
        </span>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-5 mb-4 pb-3 border-b border-slate-100">
        {Object.entries(SEVERITY).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color, boxShadow: v.glow }} />
            <span className="text-[11px] text-slate-500 font-medium">{v.label}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-5 items-start">
        {/* ========== 3D Body Model ========== */}
        <div className="relative flex-shrink-0 w-[260px] h-[400px] rounded-xl overflow-hidden bg-gradient-to-b from-slate-100/80 via-slate-50 to-slate-100/80" data-testid="body-map-3d">
          {/* 3D Human Body Image */}
          <img
            src={BODY_IMAGE}
            alt="3D Human Body Model"
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
            draggable={false}
          />

          {/* Hotspot markers for affected regions */}
          {Object.entries(HOTSPOTS).map(([key, spot]) => {
            const data = regions[key];
            if (!data) return null;
            const sev = SEVERITY[data.severity] || SEVERITY.mild;
            const isActive = active === key;

            return (
              <div key={key}>
                {/* Main hotspot */}
                <button
                  className="absolute z-10 -translate-x-1/2 -translate-y-1/2 group"
                  style={{ top: spot.top, left: spot.left }}
                  onMouseEnter={() => setActive(key)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => setActive(isActive ? null : key)}
                  data-testid={`body-region-${key}`}
                >
                  {/* Outer pulse ring */}
                  <span
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{ background: sev.color, opacity: 0.25, width: 28, height: 28, margin: '-4px' }}
                  />
                  {/* Inner dot */}
                  <span
                    className="relative block w-5 h-5 rounded-full border-2 border-white/90 shadow-lg"
                    style={{ background: sev.color, boxShadow: sev.glow }}
                  />
                </button>

                {/* Second hotspot for lungs (both sides) */}
                {spot.rightClone && (
                  <button
                    className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
                    style={{ top: spot.top, left: spot.rightClone }}
                    onMouseEnter={() => setActive(key)}
                    onMouseLeave={() => setActive(null)}
                  >
                    <span className="absolute inset-0 rounded-full animate-ping" style={{ background: sev.color, opacity: 0.25, width: 28, height: 28, margin: '-4px' }} />
                    <span className="relative block w-5 h-5 rounded-full border-2 border-white/90 shadow-lg" style={{ background: sev.color, boxShadow: sev.glow }} />
                  </button>
                )}

                {/* Floating card on hover/click */}
                {isActive && (
                  <div
                    className="absolute z-30 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-200/80 p-3 min-w-[180px] pointer-events-none"
                    style={{
                      top: `calc(${spot.top} + 18px)`,
                      left: parseInt(spot.left) > 50 ? 'auto' : `calc(${spot.left} + 18px)`,
                      right: parseInt(spot.left) > 50 ? '10px' : 'auto',
                    }}
                    data-testid="body-map-tooltip"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: sev.color }} />
                      <span className="text-xs font-bold text-slate-800">{spot.label}</span>
                      <span className={cn('text-[8px] px-1.5 py-0.5 rounded-full font-bold ml-auto', sev.badge)}>{sev.label}</span>
                    </div>
                    {data.issues?.map((issue, i) => (
                      <div key={i} className="flex items-start gap-2 py-0.5 pl-1">
                        <span className="h-1.5 w-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: SEVERITY[issue.severity]?.color || '#94a3b8' }} />
                        <div>
                          <span className="text-[11px] text-slate-700 font-medium">{issue.name}</span>
                          <span className="text-[9px] text-slate-400 ml-1">({issue.type})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ========== Issues Panel ========== */}
        <div className="flex-1 min-w-0">
          {affectedCount > 0 ? (
            <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
              {Object.entries(regions).map(([key, data]) => {
                const spot = HOTSPOTS[key];
                const sev = SEVERITY[data.severity] || SEVERITY.mild;
                if (!spot) return null;
                const isHovered = active === key;
                return (
                  <div
                    key={key}
                    className={cn(
                      'rounded-xl p-3.5 border-2 transition-all duration-200 cursor-pointer',
                      isHovered
                        ? 'bg-white shadow-lg scale-[1.01]'
                        : 'bg-white/60 hover:bg-white hover:shadow-md border-slate-100'
                    )}
                    style={isHovered ? { borderColor: sev.color + '40' } : undefined}
                    onMouseEnter={() => setActive(key)}
                    onMouseLeave={() => setActive(null)}
                    data-testid={`body-issue-${key}`}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <span className="h-3.5 w-3.5 rounded-full ring-2 ring-offset-1 shadow-sm"
                            style={{ background: sev.color, ringColor: sev.color + '30' }} />
                      <span className="text-[13px] font-bold text-slate-800">{spot.label}</span>
                      <span className={cn('text-[9px] px-2 py-0.5 rounded-full font-bold ml-auto uppercase tracking-wider', sev.badge)}>
                        {sev.label}
                      </span>
                    </div>
                    {data.issues?.map((issue, i) => (
                      <div key={i} className="flex items-center gap-2 pl-6 py-0.5">
                        <span className="h-1 w-1 rounded-full bg-slate-300" />
                        <span className="text-[11px] text-slate-600">{issue.name}</span>
                        <span className="text-[9px] text-slate-400">({issue.type})</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="h-12 w-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="h-6 w-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-slate-500 font-medium">All Clear</p>
              <p className="text-[11px] text-slate-400 mt-1">No health issues mapped to body regions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BodyMap;
